// Canvas-based waveform viewer + trim-marker editor.
//
// The pad is the single shared primitive every forge verb uses: Record /
// Bounce / Chop / Mangle all feed their resulting buffer in here and get
// consistent keyboard + marker UX for free.
//
// Responsibilities:
//   - Render the waveform at the current zoom/pan, one vertical span per
//     canvas column, min/max per channel. Peaks are computed off-thread.
//   - Expose in/out markers as controlled props, with click-to-set and
//     keyboard nudges.
//   - Audition playback starts at the in-marker and stops at the
//     out-marker, optionally looping between them.
//   - Playhead animated off AudioContext time (not wall clock) so it
//     remains phase-accurate to the audio even when the tab is throttled.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { requestPeaks } from './peaksClient';

export type MarkerKind = 'in' | 'out';

type Props = {
  /** Source buffer. null is rendered as an empty pad. */
  buffer: AudioBuffer | null;
  /** Current in/out markers as frame indices. Controlled. */
  inMarkerFrame: number;
  outMarkerFrame: number;
  /** Optional loop-between-markers mode for audition. */
  loopEnabled?: boolean;
  /** Chop slices as frame indices (optional; used by the chop verb). */
  slices?: number[];
  /** Called when the user moves a marker. */
  onMarkerChange?: (kind: MarkerKind, frame: number) => void;
  /** Audition callbacks — pad drives audio internally but reports state. */
  onAuditionStart?: () => void;
  onAuditionStop?: () => void;
  /** Shared Strudel AudioContext (re-used so we don't create a second one). */
  audioContext?: AudioContext | null;
  height?: number;
};

const DEFAULT_HEIGHT = 160;
const MIN_BUCKETS = 64;

export function WaveformPad({
  buffer,
  inMarkerFrame,
  outMarkerFrame,
  loopEnabled,
  slices,
  onMarkerChange,
  onAuditionStart,
  onAuditionStop,
  audioContext,
  height = DEFAULT_HEIGHT,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Zoom/pan are frame-window state. We keep the window in frames so
  // changes to zoom don't shift the underlying measurement.
  const totalFrames = buffer?.length ?? 0;
  const [windowStart, setWindowStart] = useState(0);
  const [windowLength, setWindowLength] = useState(0);

  // Re-seat the window when the buffer changes. Default view = full buffer.
  useEffect(() => {
    setWindowStart(0);
    setWindowLength(totalFrames);
  }, [totalFrames]);

  // Peaks for the current window. Re-request when window or buffer or
  // canvas size changes.
  const [peaks, setPeaks] = useState<{ perChannel: Float32Array[]; buckets: number } | null>(
    null,
  );
  const [canvasWidth, setCanvasWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        if (w > 0) setCanvasWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!buffer || canvasWidth < 1 || windowLength < 1) {
      setPeaks(null);
      return;
    }
    const channels: Float32Array[] = [];
    const maxCh = Math.min(2, buffer.numberOfChannels);
    for (let c = 0; c < maxCh; c++) {
      // subarray is a view; worker handles it without a copy.
      channels.push(
        buffer.getChannelData(c).subarray(windowStart, windowStart + windowLength),
      );
    }
    const buckets = Math.max(MIN_BUCKETS, canvasWidth);
    void requestPeaks(channels, buckets).then((res) => {
      if (cancelled) return;
      setPeaks({ perChannel: res.perChannel, buckets: res.buckets });
    });
    return () => {
      cancelled = true;
    };
  }, [buffer, canvasWidth, windowStart, windowLength]);

  // Playhead. Rendered as its own prop so the draw loop can check + clear
  // without re-requesting peaks. `null` = not playing.
  const [playheadFrame, setPlayheadFrame] = useState<number | null>(null);
  const playbackRef = useRef<{
    src: AudioBufferSourceNode;
    gain: GainNode;
    startTime: number;
    beginFrame: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);

  const stopAudition = useCallback(() => {
    const pb = playbackRef.current;
    if (pb) {
      try {
        pb.src.stop();
      } catch {
        // Already stopped naturally.
      }
    }
    playbackRef.current = null;
    setPlayheadFrame(null);
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    onAuditionStop?.();
  }, [onAuditionStop]);

  const startAudition = useCallback(() => {
    if (!buffer) return;
    const ctx = audioContext;
    if (!ctx) return;
    stopAudition();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 1;
    src.connect(gain);
    gain.connect(ctx.destination);
    const begin = Math.max(0, Math.min(totalFrames, inMarkerFrame));
    const end = Math.max(begin, Math.min(totalFrames, outMarkerFrame));
    const beginSec = begin / buffer.sampleRate;
    const endSec = end / buffer.sampleRate;
    if (loopEnabled) {
      src.loop = true;
      src.loopStart = beginSec;
      src.loopEnd = endSec;
    }
    src.start(0, beginSec);
    if (!loopEnabled && endSec > beginSec) {
      src.stop(ctx.currentTime + (endSec - beginSec));
    }
    src.onended = () => {
      if (playbackRef.current?.src === src) stopAudition();
    };
    playbackRef.current = {
      src,
      gain,
      startTime: ctx.currentTime,
      beginFrame: begin,
    };
    onAuditionStart?.();

    const tick = () => {
      const pb = playbackRef.current;
      if (!pb || !buffer) return;
      const elapsed = (ctx.currentTime - pb.startTime) * buffer.sampleRate;
      let f = pb.beginFrame + elapsed;
      if (loopEnabled && outMarkerFrame > inMarkerFrame) {
        const span = outMarkerFrame - inMarkerFrame;
        f = inMarkerFrame + ((f - inMarkerFrame) % span);
      }
      setPlayheadFrame(f);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [
    buffer,
    audioContext,
    inMarkerFrame,
    outMarkerFrame,
    loopEnabled,
    totalFrames,
    stopAudition,
    onAuditionStart,
  ]);

  useEffect(() => () => stopAudition(), [stopAudition]);

  // Draw. Re-runs whenever peaks / markers / playhead / dimensions change.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvasWidth;
    const h = height;
    if (canvas.width !== w * dpr) canvas.width = Math.max(1, Math.floor(w * dpr));
    if (canvas.height !== h * dpr) canvas.height = Math.max(1, Math.floor(h * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Background + channel divider.
    ctx.fillStyle = '#09090B'; // chassis-base
    ctx.fillRect(0, 0, w, h);

    if (!peaks || !buffer || windowLength < 1) {
      // Empty state.
      ctx.fillStyle = '#52525B';
      ctx.font = '11px ui-monospace, monospace';
      ctx.fillText('(drop or record to load a buffer)', 12, h / 2);
      return;
    }

    const channelCount = peaks.perChannel.length;
    const laneHeight = h / channelCount;

    // Waveform — one vertical span per bucket, mapped to canvas column.
    for (let c = 0; c < channelCount; c++) {
      const ch = peaks.perChannel[c]!;
      const midY = laneHeight * c + laneHeight / 2;
      const amp = laneHeight * 0.45;
      ctx.fillStyle = '#FDE047'; // accent
      for (let x = 0; x < w; x++) {
        // Linear map canvas pixel → bucket index.
        const b = Math.floor((x * peaks.buckets) / w);
        const min = ch[b * 2]!;
        const max = ch[b * 2 + 1]!;
        const yTop = midY - max * amp;
        const yBot = midY - min * amp;
        ctx.fillRect(x, yTop, 1, Math.max(1, yBot - yTop));
      }
      if (c < channelCount - 1) {
        ctx.fillStyle = '#27272A';
        ctx.fillRect(0, laneHeight * (c + 1), w, 1);
      }
    }

    // Loop region shading between markers when loop is on.
    const windowEnd = windowStart + windowLength;
    const frameToX = (frame: number): number => {
      if (frame <= windowStart) return 0;
      if (frame >= windowEnd) return w;
      return ((frame - windowStart) * w) / windowLength;
    };

    if (loopEnabled) {
      ctx.fillStyle = 'rgba(253, 224, 71, 0.08)';
      const x1 = frameToX(inMarkerFrame);
      const x2 = frameToX(outMarkerFrame);
      ctx.fillRect(Math.min(x1, x2), 0, Math.abs(x2 - x1), h);
    }

    // Slice markers.
    if (slices && slices.length > 0) {
      ctx.strokeStyle = '#60A5FA'; // blue-400
      ctx.lineWidth = 1;
      for (const s of slices) {
        const x = frameToX(s);
        if (x < 0 || x > w) continue;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
    }

    // In / out markers.
    const drawMarker = (frame: number, color: string, label: string) => {
      const x = frameToX(frame);
      if (x < -10 || x > w + 10) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillRect(x - 6, 0, 12, 12);
      ctx.fillStyle = '#09090B';
      ctx.font = 'bold 9px ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x, 6);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
    };
    drawMarker(inMarkerFrame, '#10B981', 'I'); // emerald-500
    drawMarker(outMarkerFrame, '#F43F5E', 'O'); // rose-500

    // Playhead.
    if (playheadFrame !== null) {
      const x = frameToX(playheadFrame);
      if (x >= 0 && x <= w) {
        ctx.strokeStyle = '#FEF08A';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
    }
  }, [
    canvasWidth,
    height,
    peaks,
    buffer,
    windowStart,
    windowLength,
    inMarkerFrame,
    outMarkerFrame,
    loopEnabled,
    slices,
    playheadFrame,
  ]);

  // Pixel → frame conversion. Centralised so mouse + wheel + keys agree.
  const pixelToFrame = useCallback(
    (pixelX: number): number => {
      if (canvasWidth < 1 || windowLength < 1) return 0;
      const frac = Math.max(0, Math.min(1, pixelX / canvasWidth));
      return Math.round(windowStart + frac * windowLength);
    },
    [canvasWidth, windowStart, windowLength],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const frame = pixelToFrame(e.clientX - rect.left);
      // Shift / right-click sets out marker; default sets in.
      if (e.shiftKey || e.button === 2) {
        onMarkerChange?.('out', frame);
      } else {
        onMarkerChange?.('in', frame);
      }
    },
    [onMarkerChange, pixelToFrame],
  );

  // Wheel: zoom when Cmd/Ctrl held, pan otherwise. Standard CodeMirror
  // mental model — wheel by default scrolls, modifier zooms.
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      if (!buffer) return;
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        const zoomIn = e.deltaY < 0;
        const factor = zoomIn ? 0.8 : 1.25;
        const rect = e.currentTarget.getBoundingClientRect();
        const anchorFrame = pixelToFrame(e.clientX - rect.left);
        const newLength = Math.max(
          64,
          Math.min(totalFrames, Math.round(windowLength * factor)),
        );
        // Keep the anchor frame under the cursor stable.
        const fracUnderCursor = (anchorFrame - windowStart) / Math.max(1, windowLength);
        const newStart = Math.max(
          0,
          Math.min(
            totalFrames - newLength,
            Math.round(anchorFrame - fracUnderCursor * newLength),
          ),
        );
        setWindowStart(newStart);
        setWindowLength(newLength);
      } else if (windowLength < totalFrames) {
        e.preventDefault();
        const delta = Math.round((e.deltaY * windowLength) / canvasWidth);
        setWindowStart((prev) =>
          Math.max(0, Math.min(totalFrames - windowLength, prev + delta)),
        );
      }
    },
    [buffer, totalFrames, windowLength, windowStart, pixelToFrame, canvasWidth],
  );

  const nudgeMarker = useCallback(
    (kind: MarkerKind, delta: number) => {
      const current = kind === 'in' ? inMarkerFrame : outMarkerFrame;
      const next = Math.max(0, Math.min(totalFrames, current + delta));
      onMarkerChange?.(kind, next);
    },
    [inMarkerFrame, outMarkerFrame, totalFrames, onMarkerChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!buffer) return;
      const coarse = e.shiftKey;
      const step = coarse ? Math.max(1, Math.round(totalFrames / 100)) : Math.max(1, Math.round(totalFrames / 1000));
      switch (e.key) {
        case '[':
          e.preventDefault();
          nudgeMarker('in', -step);
          break;
        case ']':
          e.preventDefault();
          nudgeMarker('in', step);
          break;
        case '{':
          e.preventDefault();
          nudgeMarker('out', -step);
          break;
        case '}':
          e.preventDefault();
          nudgeMarker('out', step);
          break;
        case ' ':
          e.preventDefault();
          if (playbackRef.current) stopAudition();
          else startAudition();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          onMarkerChange?.('in', 0);
          onMarkerChange?.('out', totalFrames);
          setWindowStart(0);
          setWindowLength(totalFrames);
          break;
        case 'Escape':
          if (playbackRef.current) {
            e.preventDefault();
            stopAudition();
          }
          break;
      }
    },
    [buffer, totalFrames, nudgeMarker, startAudition, stopAudition, onMarkerChange],
  );

  const durationSec = useMemo(() => {
    if (!buffer) return 0;
    return buffer.length / buffer.sampleRate;
  }, [buffer]);

  const markerStatus = useMemo(() => {
    if (!buffer) return '';
    const inSec = inMarkerFrame / buffer.sampleRate;
    const outSec = outMarkerFrame / buffer.sampleRate;
    return `in ${inSec.toFixed(3)}s · out ${outSec.toFixed(3)}s · len ${durationSec.toFixed(3)}s`;
  }, [buffer, inMarkerFrame, outMarkerFrame, durationSec]);

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="flex flex-col w-full outline-none focus-visible:ring-1 focus-visible:ring-yellow-300/40"
      role="group"
      aria-label="Waveform editor"
    >
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onContextMenu={(e) => {
          e.preventDefault();
          handleClick(e);
        }}
        onWheel={handleWheel}
        style={{ width: '100%', height }}
        className="block bg-zinc-950 border border-zinc-800"
      />
      <div className="flex justify-between items-center px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-900 border-x border-b border-zinc-800">
        <div>{markerStatus}</div>
        <div>
          [ ] in · {'{'} {'}'} out · space audition · r reset · ⌘wheel zoom
        </div>
      </div>
    </div>
  );
}
